import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { downscaleImage } from "../lib/img";
import { enablePush, pushSupported, pushPermission } from "../lib/push";
import {
  UserRound, Lock, Globe2, HandHeart, Mail, Flag, MapPin, ShieldCheck,
  HelpCircle, UserPlus, MinusCircle, LogOut, ChevronRight, Camera, Loader2, Bell, X, FileText } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

const NAVY = "#0d2340";
const GOLD = "#E0A73A";
const APP_VERSION = "1.2.0";

const NOTIF_ROWS = [
  { key: "prayer", icon: HandHeart, tint: "rgba(224,167,58,0.15)", color: "#B07A17", title: "Prayer Requests", desc: "Receive urgent prayer needs" },
  { key: "dispatches", icon: Mail, tint: "rgba(13,35,64,0.08)", color: NAVY, title: "New Dispatches", desc: "Get new stories from the journey" },
  { key: "milestones", icon: Flag, tint: "rgba(34,139,87,0.12)", color: "#228B57", title: "Milestones", desc: "Important moments to celebrate" },
  { key: "arrivals", icon: MapPin, tint: "rgba(13,35,64,0.08)", color: NAVY, title: "Arrival Updates", desc: "Know when we've safely arrived" },
];

export default function MyAccount() {
  const { user, org, config, logout, refreshMe } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [prefs, setPrefs] = useState({ prayer: true, dispatches: true, milestones: true, arrivals: true, email_updates: true });
  const [uploading, setUploading] = useState(false);
  const [pushState, setPushState] = useState("idle"); // idle | on | unavailable
  const [dialog, setDialog] = useState(null); // "profile" | "password" | "missions"
  const [leaveOpen, setLeaveOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/auth/notification-prefs"); setPrefs(data); }
      catch (e) { console.debug("notification prefs load failed", e?.message); }
    })();
    if (!pushSupported()) setPushState("unavailable");
    else if (pushPermission() === "granted" && localStorage.getItem("mc_fcm_token")) setPushState("on");
    else setPushState("idle");
  }, []);

  const savePref = async (key, value) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    try { await api.put("/auth/notification-prefs", { [key]: value }); }
    catch (e) { setPrefs(prefs); toast.error(apiErr(e)); }
  };

  const enableAll = async () => {
    const all = { prayer: true, dispatches: true, milestones: true, arrivals: true };
    setPrefs(all);
    try { await api.put("/auth/notification-prefs", all); toast.success("All notifications on"); }
    catch (e) { toast.error(apiErr(e)); }
    if (pushState === "idle") { try { await enablePush(); setPushState("on"); } catch (_) { /* optional */ } }
  };

  const turnOnPush = async () => {
    try { await enablePush(); setPushState("on"); toast.success("Push notifications enabled"); }
    catch (e) { toast.error(e.message || "Couldn't enable push"); }
  };

  const pickPhoto = () => fileRef.current?.click();
  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const small = await downscaleImage(file, 512, 0.85);
      const fd = new FormData();
      fd.append("file", small, "photo.jpg");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/auth/profile", { photo_file_id: data.file_id });
      await refreshMe();
      toast.success("Photo updated");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploading(false); }
  };

  const inviteFriends = async () => {
    const link = `${window.location.origin}/auth?mode=join&code=${org?.join_code || ""}`;
    const text = `Join me in following ${org?.org_name || "this mission"} on MissionCompanion — pray, encourage, and celebrate what God is doing. ${link}`;
    try {
      if (navigator.share) await navigator.share({ title: org?.org_name || "MissionCompanion", text, url: link });
      else { await navigator.clipboard.writeText(text); toast.success("Invite link copied"); }
    } catch (_) { /* user cancelled */ }
  };

  const doLeave = async (password) => {
    try {
      await api.delete("/auth/account", { data: { password } });
      await logout();
      toast.success("You've left this mission");
      const s = localStorage.getItem("mc_org_slug");
      navigate(s ? `/${s}` : "/");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const photo = user?.photo_file_id ? fileUrl(`/api/files/${user.photo_file_id}`, 200) : null;

  return (
    <div className="-mx-4 -mt-4" data-testid="my-account">
      {/* Navy header */}
      <div className="text-center py-5 px-4" style={{ background: NAVY, paddingTop: "calc(env(safe-area-inset-top) + 16px)" }}>
        <h1 className="font-heading font-bold text-2xl" style={{ color: "#F7F4EE" }} data-testid="account-title">My Account</h1>
      </div>

      <div className="px-4 pt-4 pb-8 space-y-7" style={{ background: "#F7F4EE" }}>
        {/* Profile card */}
        <div className="bg-white rounded-3xl p-5 shadow-[0_4px_24px_rgba(13,35,64,0.05)] flex items-center gap-4">
          <div className="relative shrink-0">
            {photo ? (
              <img src={photo} alt="" className="h-20 w-20 rounded-full object-cover" />
            ) : (
              <div className="h-20 w-20 rounded-full flex items-center justify-center text-white font-heading font-bold text-3xl" style={{ background: NAVY }}>
                {(user?.name || "?")[0]}
              </div>
            )}
            <button data-testid="account-photo-btn" onClick={pickPhoto} disabled={uploading}
              className="absolute -bottom-0.5 -right-0.5 h-8 w-8 rounded-full flex items-center justify-center text-white shadow-md" style={{ background: NAVY }}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="account-photo-input" />
          </div>
          <div className="min-w-0">
            <div className="font-heading font-bold text-2xl leading-tight truncate" style={{ color: NAVY }} data-testid="account-name">{user?.name || "Friend"}</div>
            <div className="text-sm truncate" style={{ color: "#4A5D73" }} data-testid="account-email">{user?.email}</div>
            <button data-testid="account-view-profile" onClick={() => setDialog("profile")} className="mt-1 flex items-center gap-1 text-sm font-semibold" style={{ color: "#2563eb" }}>
              View my profile <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* MY INFORMATION */}
        <Section label="My Information">
          <Row icon={UserRound} tint="rgba(37,99,235,0.10)" color="#2563eb" title="Personal Information" desc="Manage your name, email and profile photo" onClick={() => setDialog("profile")} testid="row-personal" />
          <Row icon={Lock} tint="rgba(224,167,58,0.15)" color="#B07A17" title="Password" desc="Change your password" onClick={() => setDialog("password")} testid="row-password" />
          <Row icon={Globe2} tint="rgba(37,99,235,0.10)" color="#2563eb" title="Connected Missions" desc="View and manage missions you follow" onClick={() => setDialog("missions")} testid="row-missions" last />
        </Section>

        {/* STAY CLOSE TO THE JOURNEY */}
        <div>
          <div className="text-xs uppercase tracking-[0.15em] font-semibold px-1" style={{ color: NAVY }}>Stay Close to the Journey</div>
          <p className="text-sm px-1 mt-0.5 mb-3" style={{ color: "#4A5D73" }}>Choose how you'd like to stay connected.</p>
          <div className="bg-white rounded-3xl overflow-hidden shadow-[0_4px_24px_rgba(13,35,64,0.05)]">
            {pushState === "idle" && (
              <button data-testid="account-enable-push" onClick={turnOnPush} className="w-full flex items-center gap-3 px-5 py-3.5 border-b border-[#0d2340]/8 text-left" style={{ background: "rgba(224,167,58,0.08)" }}>
                <Bell className="h-5 w-5" style={{ color: "#B07A17" }} />
                <span className="flex-1 text-sm font-medium" style={{ color: NAVY }}>Turn on push notifications to receive these</span>
                <span className="text-sm font-semibold" style={{ color: "#B07A17" }}>Enable</span>
              </button>
            )}
            {NOTIF_ROWS.map((r, i) => (
              <div key={r.key} className={`flex items-center gap-4 px-5 py-4 ${i < NOTIF_ROWS.length - 1 ? "border-b border-[#0d2340]/6" : ""}`}>
                <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0" style={{ background: r.tint }}>
                  <r.icon className="h-5 w-5" style={{ color: r.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-base leading-tight" style={{ color: NAVY }}>{r.title}</div>
                  <div className="text-sm" style={{ color: "#4A5D73" }}>{r.desc}</div>
                </div>
                <Switch data-testid={`notif-toggle-${r.key}`} checked={prefs[r.key] !== false} onCheckedChange={(v) => savePref(r.key, v)} />
              </div>
            ))}
            <div className="flex items-center justify-between px-5 py-4 border-t border-[#0d2340]/6">
              <div>
                <div className="font-heading font-bold text-base" style={{ color: NAVY }}>Enable All</div>
                <div className="text-sm" style={{ color: "#4A5D73" }}>Turn on all notifications</div>
              </div>
              <button data-testid="account-enable-all" onClick={enableAll} className="rounded-full border px-5 h-10 text-sm font-semibold" style={{ borderColor: NAVY, color: NAVY }}>Enable All</button>
            </div>
            <div className="flex items-center gap-4 px-5 py-4 border-t border-[#0d2340]/6">
              <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(13,35,64,0.08)" }}>
                <Mail className="h-5 w-5" style={{ color: NAVY }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold text-base leading-tight" style={{ color: NAVY }}>Email updates</div>
                <div className="text-sm" style={{ color: "#4A5D73" }}>Announcements &amp; dispatches sent to your inbox</div>
              </div>
              <Switch data-testid="notif-toggle-email_updates" checked={prefs.email_updates !== false} onCheckedChange={(v) => savePref("email_updates", v)} />
            </div>
          </div>
        </div>

        {/* PREFERENCES */}
        <Section label="Preferences">
          <Row icon={Globe2} tint="rgba(37,99,235,0.10)" color="#2563eb" title="Language" desc="Choose your preferred language"
            right={<span className="text-sm font-semibold" style={{ color: NAVY }}>English</span>}
            onClick={() => toast("More languages coming soon")} testid="row-language" />
          <Row icon={ShieldCheck} tint="rgba(13,35,64,0.08)" color={NAVY} title="Privacy" desc="Manage your privacy settings" onClick={() => toast("Privacy settings coming soon")} testid="row-privacy" last />
        </Section>

        {/* JOURNEY */}
        {config?.id && (
          <Section label="Journey">
            <Row icon={FileText} tint="rgba(224,167,58,0.14)" color="#B87309" title="Trip report" desc={`The story, stats and highlights of ${config.trip_name}`}
              onClick={() => navigate(`/app/trips/${config.id}/report`)} testid="row-trip-report" last />
          </Section>
        )}

        {/* SUPPORT & COMMUNITY */}
        <Section label="Support & Community">
          <Row icon={HelpCircle} tint="rgba(37,99,235,0.10)" color="#2563eb" title="Support Center" desc="Get help and find answers"
            onClick={() => { window.location.href = `mailto:${org?.email || "support@missioncompanion.app"}?subject=${encodeURIComponent("MissionCompanion support")}`; }} testid="row-support" />
          <Row icon={UserPlus} tint="rgba(37,99,235,0.10)" color="#2563eb" title="Invite Friends" desc="Invite others to join the mission" onClick={inviteFriends} testid="row-invite" last />
        </Section>

        {/* ACCOUNT ACTIONS */}
        <Section label="Account Actions">
          <button data-testid="row-leave" onClick={() => setLeaveOpen(true)} className="w-full flex items-center gap-4 px-5 py-4 border-b border-[#0d2340]/6 text-left">
            <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(220,38,38,0.10)" }}>
              <MinusCircle className="h-5 w-5 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-base leading-tight text-red-600">Leave This Mission</div>
              <div className="text-sm" style={{ color: "#4A5D73" }}>Stop following this journey and receiving its updates</div>
            </div>
            <ChevronRight className="h-5 w-5 shrink-0" style={{ color: "#94a3b8" }} />
          </button>
          <button data-testid="row-signout" onClick={async () => { await logout(); const s = localStorage.getItem("mc_org_slug"); navigate(s ? `/${s}` : "/"); }} className="w-full flex items-center gap-4 px-5 py-4 text-left">
            <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(13,35,64,0.08)" }}>
              <LogOut className="h-5 w-5" style={{ color: NAVY }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-base leading-tight" style={{ color: NAVY }}>Sign Out</div>
              <div className="text-sm" style={{ color: "#4A5D73" }}>Sign out of your account</div>
            </div>
            <ChevronRight className="h-5 w-5 shrink-0" style={{ color: "#94a3b8" }} />
          </button>
        </Section>

        <div className="text-center text-xs pt-1" style={{ color: "#94a3b8" }}>App version {APP_VERSION}</div>
      </div>

      {dialog === "profile" && <ProfileDialog user={user} onClose={() => setDialog(null)} refreshMe={refreshMe} />}
      {dialog === "password" && <PasswordDialog onClose={() => setDialog(null)} />}
      {dialog === "missions" && <MissionsDialog org={org} onClose={() => setDialog(null)} />}
      <LeaveDialog open={leaveOpen} onOpenChange={setLeaveOpen} orgName={org?.org_name} onConfirm={doLeave} />
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.15em] font-semibold px-1 mb-3" style={{ color: NAVY }}>{label}</div>
      <div className="bg-white rounded-3xl overflow-hidden shadow-[0_4px_24px_rgba(13,35,64,0.05)]">{children}</div>
    </div>
  );
}

function Row({ icon: Icon, tint, color, title, desc, onClick, right, testid, last }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`w-full flex items-center gap-4 px-5 py-4 text-left ${last ? "" : "border-b border-[#0d2340]/6"}`}>
      <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0" style={{ background: tint }}>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold text-base leading-tight" style={{ color: NAVY }}>{title}</div>
        <div className="text-sm" style={{ color: "#4A5D73" }}>{desc}</div>
      </div>
      {right || <ChevronRight className="h-5 w-5 shrink-0" style={{ color: "#94a3b8" }} />}
    </button>
  );
}

function ProfileDialog({ user, onClose, refreshMe }) {
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [pwd, setPwd] = useState("");
  const [saving, setSaving] = useState(false);
  const emailChanged = email.trim().toLowerCase() !== (user?.email || "").toLowerCase();

  const save = async () => {
    setSaving(true);
    try {
      if (name.trim() && name.trim() !== user?.name) await api.put("/auth/profile", { name: name.trim() });
      if (emailChanged) {
        if (!pwd) { toast.error("Enter your password to change email"); setSaving(false); return; }
        await api.put("/auth/change-email", { password: pwd, new_email: email.trim() });
      }
      await refreshMe();
      toast.success("Profile updated");
      onClose();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="profile-dialog">
        <DialogHeader><DialogTitle className="font-heading">Personal Information</DialogTitle>
          <DialogDescription>Update your name, email, and profile photo.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><Label>Full name</Label><Input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" /></div>
          <div><Label>Email address</Label><Input data-testid="profile-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" /></div>
          {emailChanged && (
            <div><Label>Current password <span className="text-muted-foreground">(to confirm email change)</span></Label>
              <Input data-testid="profile-email-pwd" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} className="mt-1" placeholder="Your password" /></div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="profile-save" onClick={save} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PasswordDialog({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("Passwords don't match"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/auth/change-password", { current_password: current, new_password: next });
      if (data.token) localStorage.setItem("mc_token", data.token);
      toast.success("Password changed");
      onClose();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="password-dialog">
        <DialogHeader><DialogTitle className="font-heading">Change Password</DialogTitle>
          <DialogDescription>Choose a new password for your account.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><Label>Current password</Label><Input data-testid="pwd-current" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-1" placeholder="Leave blank if you sign in with a code" /></div>
          <div><Label>New password</Label><Input data-testid="pwd-new" type="password" value={next} onChange={(e) => setNext(e.target.value)} className="mt-1" /></div>
          <div><Label>Confirm new password</Label><Input data-testid="pwd-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="pwd-save" onClick={save} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MissionsDialog({ org, onClose }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="missions-dialog">
        <DialogHeader><DialogTitle className="font-heading">Connected Missions</DialogTitle>
          <DialogDescription>Missions you currently follow.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-3 rounded-2xl border p-4">
            {org?.logo_file_id ? (
              <img src={fileUrl(`/api/files/${org.logo_file_id}`, 200)} alt="" className="h-12 w-12 rounded-xl object-cover" />
            ) : (
              <div className="h-12 w-12 rounded-xl flex items-center justify-center text-white font-heading font-bold text-xl" style={{ background: NAVY }}>{(org?.org_name || "M")[0]}</div>
            )}
            <div className="min-w-0">
              <div className="font-heading font-bold" style={{ color: NAVY }}>{org?.org_name}</div>
              <div className="text-sm text-muted-foreground">You're following this mission</div>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">To stop following, use "Leave This Mission" in Account Actions.</p>
        </div>
        <DialogFooter><Button onClick={onClose}>Done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LeaveDialog({ open, onOpenChange, orgName, onConfirm }) {
  const [pwd, setPwd] = useState("");
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="leave-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Leave {orgName || "this mission"}?</AlertDialogTitle>
          <AlertDialogDescription>
            You'll stop following this journey and no longer receive its updates. Your account will be removed. This can't be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="py-1">
          <Label className="text-xs">Password (optional, to confirm)</Label>
          <Input data-testid="leave-pwd" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} className="mt-1" placeholder="Your password" />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="leave-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="leave-confirm" onClick={() => onConfirm(pwd)} className="bg-red-600 hover:bg-red-700">Leave mission</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
